export default {
    title: 'Medications',
    subtitle: 'Taken today: {taken} of {total}',
    newMedication: 'New medication',
    today: {
        title: 'Today',
        nothing: 'No doses today.',
        allDone: 'All of today’s doses ticked off.',
    },
    list: {
        title: 'Your medications',
        until: 'until {date}',
        from: 'from {date}',
        finished: 'course finished',
        daysLeft: 'stock for {n} day | stock for {n} days',
        noStock: 'stock not counted',
        rate: '{n}% taken · 30 days',
    },
    empty: {
        title: 'No medications yet',
        text: 'Add a medication or supplement with its dose times — tick off what you took each day, and with a stock you’ll see when to buy more.',
    },
    archive: {
        title: 'Archive',
        empty: 'Nothing here.',
        offPlan: 'outside the plan',
    },
    pick: {
        title: 'Pick a medication',
        text: 'You’ll see its stock, adherence and the last days — and change what you need.',
    },
    toolbar: {
        back: 'Back to the list',
        archived: 'Archived',
        archive: 'Archive',
        restore: 'Restore',
        remove: 'Delete medication',
    },
    toast: {
        saved: 'Saved.',
        taken: 'Taken: {name}.',
        skipped: 'Skipped: {name}.',
        undone: 'Undone.',
        restocked: 'Stock: {n} pcs.',
        archived: '"{name}" archived.',
        restored: '"{name}" back on the list.',
        removed: 'Deleted "{name}".',
    },
    states: {
        pending: 'waiting',
        due: 'now',
        taken: 'taken {time}',
        takenPlain: 'taken',
        skipped: 'skipped',
        missed: 'late',
        missedPast: 'not taken',
    },
    dose: {
        take: 'Taken',
        skip: 'Skip',
        undo: 'Undo',
        offPlan: 'outside the plan',
    },
    schedule: {
        daily: 'Every day',
        weekdays: 'On chosen days',
        every_n_days: 'Every few days',
        dailyLabel: 'every day',
        everyLabel: 'every {n} days',
        joined: '{times}, {days}',
    },
    numbers: {
        title: 'How it goes',
        stock: 'Stock',
        stockValue: '{n} pcs',
        daysLeft: 'for {n} day | for {n} days',
        noStock: 'not counted',
        rate: 'Adherence · 30 days',
        rateOf: '{taken} of {planned} doses',
        perDay: 'doses a day: {n}',
    },
    restock: {
        title: 'Bought more',
        placeholder: 'how many',
        add: '+ bought more',
        set: 'Set stock',
    },
    edit: {
        title: 'Medication settings',
    },
    history: {
        title: 'Last days',
        empty: 'Nothing yet.',
        extra: 'outside the plan {time}',
    },
    form: {
        name: 'Name',
        namePlaceholder: 'e.g. Paracetamol, Vitamin D',
        dose: 'Dose',
        dosePlaceholder: 'e.g. 1 tablet 500 mg',
        times: 'Times',
        addTime: 'Add a time',
        removeTime: 'Remove {time}',
        days: 'Days',
        every: 'Every how many days',
        everySuffix: 'days',
        course: 'Course',
        noEnd: 'No end',
        until: 'Until',
        startOn: 'From',
        endOn: 'To',
        stock: 'Stock',
        noStock: 'Don’t count',
        countStock: 'Count pieces',
        stockPieces: 'pieces now',
        perDose: 'per dose',
        perDoseHint: 'How many pieces one dose uses — that many come off the stock after “Taken”.',
        notes: 'Notes',
        notesPlaceholder: 'e.g. after a meal',
        save: 'Save',
        add: 'Add medication',
        cancel: 'Cancel',
        needTime: 'Add at least one time.',
    },
    planner: {
        title: 'Today’s medications',
        open: 'Open Medications',
    },
    help: {
        today: {
            title: 'Today',
            text: 'Today’s doses by time. “Taken” ticks a dose off (and lowers the stock), “Skip” saves that you are not taking it on purpose.',
            more: 'The colour shows the state: grey — waiting, blue — now (from its time up to 2 hours after), green — taken with the time, light — skipped, orange — late. Undo a mistake with “Undo”.',
        },
        list: {
            title: 'Your medications',
            text: 'Each medication with its schedule in words, the end of the course, how many days the stock lasts and the adherence of the last 30 days.',
            more: 'The stock turns orange when it lasts less than a week — you’ll also get a reminder to buy more.',
        },
        add: {
            title: 'New medication',
            text: 'Name, dose in words, times, days and — if you like — a course from–to and a stock of pieces.',
            more: 'The assistant can tick a dose off too: "I took my meds" or "I took paracetamol".',
        },
        stock: {
            title: 'Stock',
            text: 'How many pieces are left. Each dose taken uses as many pieces as set “per dose”; an undone one gives them back.',
            more: 'After buying more, click “+ bought more” and enter how many pieces you added.',
        },
        history: {
            title: 'Last days',
            text: 'The doses of the last two weeks with what happened to them, and intakes outside the plan.',
            more: 'Adherence counts only the doses whose time has passed.',
        },
        schedule: {
            title: 'Times and days',
            text: 'The dose times during the day and the days: every day, on chosen weekdays or every few days (counted from the start).',
            more: 'A course with an end leaves “Today” after its last day.',
        },
    },
    tour: {
        welcome: {
            title: 'Welcome to Medications',
            description: 'Medications and supplements by the clock: tick off each dose, and the stock counts itself.',
        },
        medication: {
            title: 'A medication',
            description: 'Click a medication: stock with “+ bought more”, adherence, the last days and its settings. Archive and delete are there too.',
        },
        end: {
            title: 'That’s it',
            description: 'The guide is always under “Help” — with First steps. You also see today’s doses in the Planner and in the assistant’s Right now.',
        },
    },
    firstSteps: {
        medication: {
            title: 'First medication',
            action: 'Add a medication',
            what: 'Click “New medication”, enter the name, dose and times.',
            why: 'The doses show up in “Today”, in the Planner and in reminders.',
        },
        taken: {
            title: 'First dose',
            action: 'Tick off',
            what: 'Click “Taken” next to today’s dose.',
            why: 'You see what is taken, and adherence grows.',
        },
        stock: {
            title: 'Stock',
            action: 'Enter the stock',
            what: 'On the medication, enter how many pieces are left in the pack.',
            why: 'You’ll know a week ahead that it’s time to buy more.',
        },
    },
};
